import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { resources } from '../../core/services/resources';
import { flagFor } from '../utils/geo';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import type { GeoLocation } from '../../core/types/data-models';

interface GeoLocationMultiSelectProps {
  /** Selected country ids. `null`/`undefined` is treated as an empty array. */
  value: number[] | null | undefined;
  onChange: (ids: number[]) => void;
  label?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  /**
   * When provided, the picker only offers these country ids (e.g. the
   * geo locations a Line of Business supports). `undefined` keeps the
   * default behaviour of searching the whole catalog; an empty array means
   * nothing is selectable. In restricted mode the full allowed set is
   * loaded once and filtered client-side as the user types.
   */
  allowedIds?: number[];
}

/**
 * Multi-value version of `GeoLocationSelect`. Each picked country renders
 * as a flag-emoji + name chip; same server-side `q` filter (debounced
 * 250 ms) so big catalogs stay snappy.
 *
 * Currently-selected ids that don't appear in the page of search results
 * are fetched separately (in a single bulk call) so the chips render with
 * their proper flag + name regardless of the active search term — same
 * trick as the single-select, just batched across N ids.
 */
export function GeoLocationMultiSelect({
  value,
  onChange,
  label = 'Geo locations',
  helperText,
  required,
  disabled,
  allowedIds,
}: GeoLocationMultiSelectProps) {
  const selectedIds = useMemo<number[]>(() => value ?? [], [value]);
  // Restricted to a fixed allow-list (LOB-supported countries) vs. the
  // free catalog search. Drives whether we search server-side or just
  // load the allowed set and let MUI filter it client-side.
  const restricted = allowedIds !== undefined;

  const [inputValue, setInputValue] = useState('');
  const debouncedQ = useDebouncedValue(inputValue.trim(), 250);

  // Search results (server-side filter). Disabled in restricted mode —
  // there we never want the full catalog, only the allowed rows below.
  const list = useQuery({
    queryKey: ['geo-locations', 'multi-select', debouncedQ],
    queryFn: () =>
      resources.geoLocations.list({
        pageSize: 50,
        sort: 'name',
        filter: debouncedQ ? { q: debouncedQ } : undefined,
      }),
    enabled: !restricted,
    placeholderData: (prev) => prev,
  });

  // Allowed-set fetch (restricted mode only): pull the LOB-supported rows
  // once by id so the dropdown can show them all and filter client-side.
  const allowedKey = useMemo(
    () => (allowedIds ? [...allowedIds].sort((a, b) => a - b).join(',') : ''),
    [allowedIds],
  );
  const allowedQuery = useQuery({
    queryKey: ['geo-locations', 'allowed', allowedKey],
    queryFn: () =>
      !allowedIds || allowedIds.length === 0
        ? Promise.resolve({ data: [] as GeoLocation[] })
        : resources.geoLocations.list({
            pageSize: allowedIds.length,
            filter: { id: allowedIds.join(',') },
          }),
    enabled: restricted && (allowedIds?.length ?? 0) > 0,
    staleTime: Infinity,
  });

  // Bulk fetch of currently-selected rows so chips render even when the
  // active search wouldn't return them. Cached by sorted-id signature so
  // a stable selection reuses the same cache entry.
  const idsKey = useMemo(() => [...selectedIds].sort((a, b) => a - b).join(','), [selectedIds]);
  const selectedQuery = useQuery({
    queryKey: ['geo-locations', 'by-ids', idsKey],
    // Filter[id]= accepts a comma-separated list (multi-capable on the
    // GeoLocation controller); page size is the union of every selected
    // row so the response is one round-trip per `selectedIds` change.
    queryFn: () =>
      selectedIds.length === 0
        ? Promise.resolve({ data: [] as GeoLocation[] })
        : resources.geoLocations.list({
            pageSize: Math.max(selectedIds.length, 1),
            filter: { id: selectedIds.join(',') },
          }),
    enabled: selectedIds.length > 0,
    staleTime: Infinity,
  });

  // Merge: search results + selected rows (deduped by id). Order:
  // selected rows first (so chips render right), then non-selected matches.
  // In restricted mode the option list is the allowed set (plus any already-
  // selected rows, so chips always resolve); MUI filters it by input text.
  const options = useMemo<GeoLocation[]>(() => {
    const map = new Map<number, GeoLocation>();
    for (const o of selectedQuery.data?.data ?? []) map.set(o.id, o);
    if (restricted) {
      for (const o of allowedQuery.data?.data ?? []) {
        if (!map.has(o.id)) map.set(o.id, o);
      }
    } else {
      for (const o of list.data?.data ?? []) {
        if (!map.has(o.id)) map.set(o.id, o);
      }
    }
    return Array.from(map.values());
  }, [restricted, allowedQuery.data, list.data, selectedQuery.data]);

  const selectedOptions = useMemo<GeoLocation[]>(
    () => options.filter((o) => selectedIds.includes(o.id)),
    [options, selectedIds],
  );

  return (
    <Autocomplete<GeoLocation, true, false, false>
      multiple
      // Dropdown stays open across picks so several countries can be
      // ticked off in one go.
      disableCloseOnSelect
      fullWidth
      disabled={disabled}
      options={options}
      value={selectedOptions}
      onChange={(_, next) => onChange(next.map((o) => o.id))}
      inputValue={inputValue}
      onInputChange={(_, next, reason) => {
        // Don't react to the synthetic reset that fires after selection;
        // it would re-fetch the unfiltered catalog unnecessarily.
        if (reason === 'reset') return;
        setInputValue(next);
      }}
      // Restricted mode: let MUI filter the loaded allow-list by label.
      // Catalog mode: server-side filter, so tell MUI not to second-guess us.
      filterOptions={restricted ? undefined : (x) => x}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      loading={restricted ? allowedQuery.isFetching : list.isFetching}
      noOptionsText={
        restricted
          ? allowedQuery.isFetching
            ? 'Loading…'
            : (allowedIds?.length ?? 0) === 0
              ? 'This line of business supports no geo locations.'
              : 'No matching countries.'
          : list.isFetching
            ? 'Searching…'
            : debouncedQ
              ? 'No matching countries.'
              : 'No countries found.'
      }
      renderTags={(rows, getTagProps) =>
        rows.map((row, index) => {
          // Pull `key` out of getTagProps() so React 18 stops warning that
          // `key` shouldn't ride in a spread props bag.
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              {...tagProps}
              size="small"
              label={
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <span style={{ fontSize: 16, lineHeight: 1 }}>{flagFor(row.code)}</span>
                  <span>{row.name}</span>
                </Box>
              }
            />
          );
        })
      }
      renderOption={(props, option, { selected }) => (
        // `key` direct, not in spread, per React 18.
        <Box component="li" {...props} key={option.id}>
          {/* Checkbox per row — instant visual cue that this is multi-
              select, even before the user picks anything. */}
          <Checkbox
            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
            checkedIcon={<CheckBoxIcon fontSize="small" />}
            style={{ marginRight: 8 }}
            checked={selected}
          />
          <Typography component="span" sx={{ mr: 1, fontSize: 18, lineHeight: 1 }}>
            {flagFor(option.code)}
          </Typography>
          <Typography component="span" variant="body2" sx={{ flex: 1 }}>
            {option.name}
          </Typography>
          <Typography component="span" variant="caption" color="text.secondary">
            {option.code}
          </Typography>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {(restricted ? allowedQuery.isFetching : list.isFetching) ? (
                  <CircularProgress size={16} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
